import { Chip, Input, LoadingPlaceholder, Select, Switch, TextArea } from '@trackman/web-shared-components';
import './EditGeneral.scss';
import { BannerImage } from '../BannerImage/BannerImage';
import Subtitle from 'components/TextComps/Subtitle';
import Paragraph from 'components/TextComps/Paragraph';
import { GeneralDataState } from 'stores/Admin/Editors/ModuleEditorV2/GeneralStore';
import { AdminModuleListEntryStatus } from 'types/admin/modulesV2';
import { useGeneralStore } from 'stores/Admin/Editors/ModuleEditorV2/GeneralStore';
import { useServices } from 'index';
import { useQuery } from '@tanstack/react-query';
import { BaseSyntheticEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAdminModuleV2 } from 'queryHooks/Admin/ModulesV2/useAdminModuleV2';
import { TranslationProgressSection } from './TranslationProgressSection';

export const EditGeneral = () => {
  const organizationListService = useServices().organizationsEditorService;
  const { data: organizations, isPending: loadingOrganizations } = useQuery({
    queryKey: ['adminOrganizationsList'],
    queryFn: () => organizationListService.getOrganizations(),
    refetchOnWindowFocus: false,
  });

  const {
    imageId,
    title,
    status,
    shortDescription,
    fullDescription,
    loading,
    initializing,
    isOrganizationOnly,
    allowedOrganizationIds,
  }: GeneralDataState & { loading: boolean; initializing: boolean } = useGeneralStore((state) => state);
  const setImageId = useGeneralStore((state) => state.setImageId);
  const setTitle = useGeneralStore((state) => state.setTitle);
  const setStatus = useGeneralStore((state) => state.setStatus);
  const setShortDescription = useGeneralStore((state) => state.setShortDescription);
  const setFullDescription = useGeneralStore((state) => state.setFullDescription);
  const setIsOrganizationOnly = useGeneralStore((state) => state.setIsOrganizationOnly);
  const setAllowedOrganizationIds = useGeneralStore((state) => state.setAllowedOrganizationIds);

  const service = useServices().mediaLibraryService;
  const { data } = useQuery({
    queryKey: ['moduleBackgroundImage', imageId],
    queryFn: () => service.getMedia(imageId),
    enabled: !!imageId,
    staleTime: Infinity,
  });
  const moduleId = useParams().moduleId ?? '';
  const { data: module } = useAdminModuleV2(moduleId);

  const [lastSelectedOrgName, setLastSelectedOrgName] = useState<string>('');
  const selectedOrganizations = (organizations ?? []).filter((org) => allowedOrganizationIds?.includes(org.id ?? ''));

  const moduleStatusOptions: AdminModuleListEntryStatus[] = ['Visible', 'ComingSoon', 'Hidden'];

  const handleRenderValue = (selected: string[]) => {
    const ids = selected;
    if (ids.length === 0) {
      return 'Select organizations';
    }
    const lastId = ids[ids.length - 1];
    const lastOrg = (organizations ?? []).find((org) => org.id === lastId);
    return lastOrg?.name ?? '';
  };

  const handleOnChange = (value: string[]) => {
    const newIds = value;
    const addedId = newIds.find((id) => !(allowedOrganizationIds ?? []).includes(id));
    const removedId = (allowedOrganizationIds ?? []).find((id) => !newIds.includes(id));
    const changedId = addedId ?? removedId;
    const changedOrg = (organizations ?? []).find((org) => org.id === changedId);
    if (changedOrg) {
      setLastSelectedOrgName(changedOrg.name);
      setAllowedOrganizationIds(newIds);
    }
  };

  return (
    <div className='edit-admin-module'>
      {!initializing && !loadingOrganizations ? (
        <>
          <div className='section-wrapper'>
            <Subtitle>
              <>Status</>
            </Subtitle>
            <Select
              label='Select a status'
              value={status as string}
              required
              selectOptions={moduleStatusOptions.map((option) => ({
                label: option === 'ComingSoon' ? 'Coming Soon' : option,
                value: option,
              }))}
              disabled={loading}
              onChange={(e) => setStatus(e.target.value as AdminModuleListEntryStatus)}
            />
          </div>

          <div className='section-wrapper'>
            <div className='module-restriction'>
              <Switch label='' checked={isOrganizationOnly} onChange={(e: BaseSyntheticEvent) => setIsOrganizationOnly(e.target.checked)} />
              <div className='module-restriction-label'>
                <div>Restrict module access</div>
                <div>When switched off the module is available to all TMU users.</div>
              </div>
            </div>

            {isOrganizationOnly && (
              <div className='allowed-organizations'>
                {selectedOrganizations.length > 0 && (
                  <div className='selected-organizations-chips'>
                    {selectedOrganizations.map((org) => (
                      <Chip
                        className='organization-chip'
                        key={org.id}
                        label={org.name}
                        onDelete={() => setAllowedOrganizationIds(allowedOrganizationIds?.filter((id) => id !== org.id))}
                      />
                    ))}
                    <Chip
                      label='Clear all'
                      className='clear-all-chip'
                      onClick={() => {
                        setAllowedOrganizationIds([]);
                        setLastSelectedOrgName('');
                      }}
                    />
                  </div>
                )}
                <Select
                  label={lastSelectedOrgName || 'Select organizations'}
                  multiple
                  value={allowedOrganizationIds ?? []}
                  selectOptions={(organizations ?? []).map((org) => ({ label: org.name, value: org.id ?? '' }))}
                  renderValue={(selected) => handleRenderValue(selected as string[])}
                  onChange={(e) => handleOnChange(e.target.value as string[])}
                  MenuProps={{ sx: { '& .MuiPaper-root': { maxHeight: 320 } } }}
                />
              </div>
            )}
          </div>

          <div className='section-wrapper'>
            <Subtitle>
              <>Title & Description</>
            </Subtitle>
            <Input label='Title' required value={title} disabled={loading} onChange={(e) => setTitle(e.target.value)} />
            <Input
              label='Short Description'
              required
              value={shortDescription}
              disabled={loading}
              onChange={(e) => setShortDescription(e.target.value)}
            />
            <TextArea
              label='Long Description'
              required
              value={fullDescription}
              disabled={loading}
              onChange={(e) => setFullDescription(e.target.value)}
            />
          </div>

          <div className='section-wrapper'>
            <div>
              <Subtitle>
                <>Image*</>
              </Subtitle>
              <Paragraph>
                <>
                  This image will show in the card and banner on the <span style={{ fontWeight: 600 }}>Module Overview</span> and{' '}
                  <span style={{ fontWeight: 600 }}>Module Details</span> pages
                </>
              </Paragraph>
            </div>
            <BannerImage image={data} disabled={loading} handleSelectedImage={setImageId} />
          </div>

          <TranslationProgressSection moduleId={moduleId} progress={module?.translationProgress} />
        </>
      ) : (
        <LoadingPlaceholder />
      )}
    </div>
  );
};
